import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import getQueryParameters from './getQueryParameters';
import type { Controller, PageState } from './types';

type UseFetchDataParams = {
  controller: Controller;
  pageState: PageState;
};

const useFetchData = ({ controller, pageState }: UseFetchDataParams) => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  // Read the store reference (stable) instead of subscribing via useSelector.
  // Subscribing to the entire `app` slice would re-render every page on every
  // unrelated app mutation and recreate fetchDataCallback's deps each time.
  const store = useStore<Record<string, { data?: unknown } | undefined> & { app?: unknown }>();

  // Track if we've already initiated a fetch for this location to prevent
  // duplicate dispatches across StrictMode double-renders / re-renders.
  const fetchInitiatedRef = useRef<boolean>(false);
  const lastLocationRef = useRef<string>('');

  const fetchDataCallback = useCallback(() => {
    const queryParams = getQueryParameters(location);
    const snapshot = store.getState();
    (dispatch as (a: unknown) => unknown)(
      controller.fetchData({
        location,
        params,
        queryParams,
        data: snapshot[controller.name]?.data,
        appState: snapshot.app,
      }),
    );
  }, [controller, location, params, store, dispatch]);

  useEffect(() => {
    const currentLocationKey = `${location.pathname}${location.search}${location.hash ?? ''}`;

    if (lastLocationRef.current !== currentLocationKey) {
      fetchInitiatedRef.current = false;
      lastLocationRef.current = currentLocationKey;
    }

    const shouldFetch =
      ((!pageState.loaded && !pageState.fetching) || pageState.location !== location) &&
      !fetchInitiatedRef.current;

    if (shouldFetch) {
      fetchInitiatedRef.current = true;
      fetchDataCallback();
    }
  }, [pageState.loaded, pageState.fetching, pageState.location, location, fetchDataCallback]);
};

export default useFetchData;
